/**
 * Конвенция именования шрифтов — единая для фронтенда и бэкенда.
 *
 * Формат файла: {fontName}-{weight}[Italic].ttf
 * fontName: семейство без пробелов ("Montserrat", "PlayfairDisplay")
 * fontWeight: PascalCase ("Regular", "Bold", "Medium", "SemiBold")
 *
 */

/**
 * Нормализация весов шрифтов: CSS keywords/numeric → PascalCase.
 * Идентична таблице WEIGHT_NORMALIZE на бэкенде.
 */
export const WEIGHT_NORMALIZE: Record<string, string> = {
  // CSS keywords
  normal: 'Regular',
  regular: 'Regular',
  light: 'Light',
  medium: 'Medium',
  semibold: 'SemiBold',
  bold: 'Bold',
  thin: 'Thin',
  extralight: 'ExtraLight',
  extrabold: 'ExtraBold',
  black: 'Black',
  heavy: 'Black',
  // CSS numeric weights
  '100': 'Thin',
  '200': 'ExtraLight',
  '300': 'Light',
  '400': 'Regular',
  '500': 'Medium',
  '600': 'SemiBold',
  '700': 'Bold',
  '800': 'ExtraBold',
  '900': 'Black',
};

/** Все допустимые веса в PascalCase */
const KNOWN_WEIGHTS = new Set(['Thin', 'ExtraLight', 'Light', 'Regular', 'Medium', 'SemiBold', 'Bold', 'ExtraBold', 'Black']);

/**
 * Нормализует вес шрифта в PascalCase.
 * "medium" → "Medium", "700" → "Bold", "SemiBold" → "SemiBold"
 */
export function normalizeWeight(raw: string): string {
  if (!raw) return 'Regular';

  // Уже в PascalCase — вернуть как есть
  if (KNOWN_WEIGHTS.has(raw)) return raw;

  return WEIGHT_NORMALIZE[raw.toLowerCase()] ?? 'Regular';
}

/** Канонический токен, не CSS-слово "bold"/"normal": по нему движок ставит ось wght. */
export function boldToggleWeight(bold: boolean): 'Bold' | 'Regular' {
  return bold ? 'Bold' : 'Regular';
}

/**
 * Ключ для СОПОСТАВЛЕНИЯ веса: normalizeWeight схлопывает любой нераспознанный вес в Regular,
 * из-за чего расширенное начертание ("SemiBold Condensed") отвечало бы на запрос Regular.
 */
export function weightMatchKey(raw: string | undefined): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return 'Regular';
  return WEIGHT_NORMALIZE[trimmed.toLowerCase()] ?? trimmed.toLowerCase();
}

/**
 * Формирует имя файла шрифта по конвенции бэкенда.
 * ("Montserrat", "Medium", false) → "Montserrat-Medium.ttf"
 * ("Montserrat", "Medium", true)  → "Montserrat-MediumItalic.ttf"
 */
export function buildFontFileName(fontName: string, fontWeight: string, italic: boolean): string {
  const normalizedName = fontName.replace(/\s+/g, '');
  const normalizedWeight = normalizeWeight(fontWeight);
  const italicSuffix = italic ? 'Italic' : '';
  return `${normalizedName}-${normalizedWeight}${italicSuffix}.ttf`;
}
